import api from './api';

export interface SchoolSubject {
  id: number;
  name: string;
}

/** Un "indirizzo" di studio (es. liceo scientifico). */
export interface School {
  id: number;
  name: string;
  subjects: SchoolSubject[];
}

/** Albero restituito da GET /school: livello -> tipologia -> indirizzi. */
export type SchoolTree = Record<string, Record<string, School[]>>;

export interface SchoolMatch {
  level: string;
  type: string;
  school: School;
}

/** Dati scuola di uno studente, così come arrivano dal backend. */
export interface StudentSchoolData {
  school_id?: number | null;
  school_name?: string | null;
  school_level?: string | null;
  school_class?: string | null;
}

export const CATEGORY_LABELS: Record<string, string> = {
  hs: 'Scuola Superiore',
  ms: 'Scuola Media',
  ps: 'Scuola Elementare',
};

export const CATEGORY_ORDER = ['hs', 'ms', 'ps'];

const YEAR_LABELS = ['Primo anno', 'Secondo anno', 'Terzo anno', 'Quarto anno', 'Quinto anno'];

/** Numero di anni per livello scolastico. */
const YEARS_PER_LEVEL: Record<string, number> = {
  hs: 5,
  ms: 3,
  ps: 5,
};

/** Tipi di notifica gestiti dalla modale di inizio anno scolastico. */
export const SCHOOL_YEAR_PROMOTED = 'school_year_promoted';
export const SCHOOL_YEAR_SCHOOL_RESET = 'school_year_school_reset';
export const SCHOOL_YEAR_TYPES = [SCHOOL_YEAR_PROMOTED, SCHOOL_YEAR_SCHOOL_RESET];

export interface SchoolYearNotification {
  id: number;
  type: string;
  status: string;
  accept_link: string | null;
  metadata: {
    student_id: number;
    school_year: number;
  };
}

export function getAvailableYears(level: string): { value: string; label: string }[] {
  const count = YEARS_PER_LEVEL[level];
  if (!count) return [];
  return Array.from({ length: count }, (_, i) => ({
    value: String(i + 1),
    label: YEAR_LABELS[i],
  }));
}

export function getYearLabel(schoolClass?: string | null): string {
  const index = Number(schoolClass) - 1;
  return YEAR_LABELS[index] || '';
}

/** L'anno scolastico si scrive a cavallo di due anni solari: 2026 -> "2026/2027". */
export function getSchoolYearLabel(schoolYear?: number | string | null): string {
  const start = Number(schoolYear);
  if (!start) return '';
  return `${start}/${start + 1}`;
}

// Cache a livello di modulo, condivisa tra i componenti per evitare fetch ripetuti.
let schoolTreeCache: SchoolTree | null = null;
let schoolTreePromise: Promise<SchoolTree> | null = null;

export async function fetchSchoolTree(): Promise<SchoolTree> {
  if (schoolTreeCache) return schoolTreeCache;
  if (!schoolTreePromise) {
    schoolTreePromise = api
      .get('/school')
      .then((res) => {
        schoolTreeCache = res.data || {};
        return schoolTreeCache as SchoolTree;
      })
      .catch((err) => {
        schoolTreePromise = null;
        throw err;
      });
  }
  return schoolTreePromise;
}

/** Livelli presenti nell'albero, nell'ordine di visualizzazione. */
export function getAvailableCategories(tree: SchoolTree | null): string[] {
  if (!tree) return [];
  return CATEGORY_ORDER.filter((category) => tree[category]);
}

export function getSchoolTypes(tree: SchoolTree | null, level: string): string[] {
  if (!tree || !level) return [];
  return Object.keys(tree[level] || {});
}

export function getSchools(tree: SchoolTree | null, level: string, type: string): School[] {
  if (!tree || !level || !type) return [];
  return tree[level]?.[type] || [];
}

export function findSchoolById(tree: SchoolTree | null, schoolId?: number | string | null): SchoolMatch | null {
  const id = Number(schoolId);
  if (!tree || !id) return null;
  for (const level of Object.keys(tree)) {
    for (const type of Object.keys(tree[level])) {
      const school = tree[level][type].find((s) => s.id === id);
      if (school) return { level, type, school };
    }
  }
  return null;
}

/**
 * Fallback finché il backend non espone `school_id` sullo studente:
 * risale all'indirizzo partendo dal nome (e, se noto, dal livello).
 */
export function findSchoolByName(
  tree: SchoolTree | null,
  schoolName?: string | null,
  level?: string | null,
): SchoolMatch | null {
  if (!tree || !schoolName) return null;
  const target = schoolName.trim().toLowerCase();
  const levels = level && tree[level] ? [level] : Object.keys(tree);
  for (const lvl of levels) {
    for (const type of Object.keys(tree[lvl])) {
      const school = tree[lvl][type].find((s) => s.name.trim().toLowerCase() === target);
      if (school) return { level: lvl, type, school };
    }
  }
  return null;
}

/** Uno studente è prenotabile solo se scuola e anno sono compilati. */
export function isSchoolConfigured(student?: StudentSchoolData | null): boolean {
  if (!student) return false;
  const hasSchool = !!student.school_id || !!(student.school_name && String(student.school_name).trim());
  return hasSchool && !!(student.school_class && String(student.school_class).trim());
}

/**
 * Vero solo se sappiamo con certezza che il figlio non ha ancora materie:
 * `lesson/available-sons` le restituisce in `student.subjects`. Se il campo
 * mancasse la risposta è `false`, così il flusso resta quello di prima.
 */
export function hasNoSubjects(son: any): boolean {
  const subjects = son?.student?.subjects;
  return Array.isArray(subjects) && subjects.length === 0;
}

/**
 * Una notifica di inizio anno resta da gestire finché non viene accettata:
 * "read" significa solo che la famiglia l'ha ignorata per il momento.
 */
export function isSchoolYearPending(notification: { type: string; status: string }): boolean {
  return SCHOOL_YEAR_TYPES.includes(notification.type) && notification.status !== 'accepted';
}

/**
 * Aggiorna i dati scuola dello studente.
 * `subject_ids` distingue due casi opposti: se omesso le materie già associate
 * restano invariate (cambio anno), se vale `[]` vengono azzerate (ri-configurazione).
 */
export function saveStudentSchool(
  studentId: number,
  data: { school_id?: number | null; school_class: string; subject_ids?: number[] },
): Promise<void> {
  const payload: { school_id?: number; school_class: string; subject_ids?: number[] } = {
    school_class: data.school_class,
  };
  if (data.school_id) payload.school_id = data.school_id;
  if (data.subject_ids !== undefined) payload.subject_ids = data.subject_ids;
  return api.patch(`/family/student/${studentId}`, payload).then(() => undefined);
}

/** Ignorata per ora: sparisce dalla campanella ma resta da gestire. */
export function markNotificationRead(notificationId: number): Promise<void> {
  return api.post(`/notification/${notificationId}/read`).then(() => undefined);
}

/** Gestita davvero: la notifica passa in "accepted" e lo studente si sblocca. */
export function acceptNotification(notificationId: number): Promise<void> {
  return api.post(`/notification/${notificationId}/accept`).then(() => undefined);
}

/*
 * Il job di inizio anno rigenera le notifiche a ogni esecuzione: per lo stesso
 * studente (e lo stesso anno) ne esistono più copie, una per batch. Chiudere solo
 * quella mostrata lasciava le altre pendenti, e lo studente bloccato per sempre in
 * prenotazione. Da qui le due regole applicate sotto: in lettura conta solo la
 * notifica più recente per studente, in scrittura si agisce su tutte le sue pendenti.
 */

/** `/notification` è paginato: senza page_size si vedrebbero solo le ultime 20. */
const NOTIFICATIONS_PAGE_SIZE = 200;

/** Tutte le notifiche di inizio anno con uno studente associato, duplicati compresi. */
export async function fetchSchoolYearNotifications(): Promise<SchoolYearNotification[]> {
  const first = await api.get(`/notification?page_size=${NOTIFICATIONS_PAGE_SIZE}`);
  const notifications: any[] = first.data.notifications || [];

  // Se il backend ignorasse page_size resterebbero fuori le pagine successive.
  const pageCount: number = first.data.pagination?.page_count || 1;
  if (pageCount > 1) {
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) =>
        api.get(`/notification?page_size=${NOTIFICATIONS_PAGE_SIZE}&page=${i + 2}`),
      ),
    );
    rest.forEach((res) => notifications.push(...(res.data.notifications || [])));
  }

  // Dedup per id: se il backend ignorasse anche `page` le pagine si ripeterebbero.
  const seen = new Set<number>();
  return notifications.filter((n: any) => {
    if (!SCHOOL_YEAR_TYPES.includes(n.type) || !n.metadata?.student_id || seen.has(n.id)) return false;
    seen.add(n.id);
    return true;
  }) as SchoolYearNotification[];
}

/**
 * Una sola notifica per studente, la più recente: gli id sono progressivi ma
 * l'ordine della risposta non è garantito, quindi si confrontano esplicitamente.
 */
export function latestPerStudent(notifications: SchoolYearNotification[]): SchoolYearNotification[] {
  const byStudent = new Map<number, SchoolYearNotification>();
  notifications.forEach((notification) => {
    const current = byStudent.get(notification.metadata.student_id);
    if (!current || notification.id > current.id) {
      byStudent.set(notification.metadata.student_id, notification);
    }
  });
  return Array.from(byStudent.values());
}

/** Sorgente unica dei blocchi in prenotazione: le pendenti, una per studente. */
export async function fetchPendingSchoolYearNotifications(): Promise<SchoolYearNotification[]> {
  const notifications = await fetchSchoolYearNotifications();
  return latestPerStudent(notifications).filter(isSchoolYearPending);
}

/**
 * Chiude *tutte* le notifiche di inizio anno pendenti di uno studente, non solo
 * quella mostrata: altrimenti i duplicati più vecchi lo terrebbero bloccato.
 * Un fallimento isolato non annulla gli altri; se falliscono tutti l'errore risale.
 */
export async function resolveSchoolYearForStudent(
  studentId: number,
  action: 'accept' | 'read',
): Promise<void> {
  const notifications = await fetchSchoolYearNotifications();
  const pending = notifications.filter((n) => n.metadata.student_id === studentId && isSchoolYearPending(n));
  if (pending.length === 0) return;

  const apply = action === 'accept' ? acceptNotification : markNotificationRead;
  const results = await Promise.allSettled(pending.map((n) => apply(n.id)));

  const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  failures.forEach((r) => console.error(`Errore nel chiudere una notifica di inizio anno (${action}):`, r.reason));
  if (failures.length === results.length) throw failures[0].reason;
}

/*
 * Nota su `accept_link`: il backend lo valorizza con un URL del frontend
 * (es. https://dev.formandopercorsi.com/family/school-year/confirm?student_id=X).
 * È un deep link a una pagina — pensato per le email — non un endpoint da
 * chiamare: invocarlo via XHR risponde 405 al preflight. La pagina corrispondente
 * è src/pages/family/SchoolYearConfirm.tsx.
 */
